<script lang="ts" setup>
import { AxiosResponse } from 'axios';
import useVuelidate, { Validation, ValidationArgs } from '@vuelidate/core';
import { maxLength, minLength, required } from '@vuelidate/validators';
import { computed, onMounted, reactive, Ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { toast, toastServiceError } from '@/services';
import { useUiStore } from '@/stores';
import { TrashIcon } from '@heroicons/vue/outline';
import ScriptService from '@/services/api/ScriptService';
import {
    FormAction,
    MultiSelectOption,
    Permission,
    ScriptFormState,
    ScriptNumberFormState,
} from '@/types';

const { t } = useI18n();
const uiStore = useUiStore();
const route = useRoute();
const router = useRouter();

const props = defineProps({
    action: { type: String, default: FormAction.CREATE },
});

const mediaOptions = [
    {
        label: t('script.edit.form.media.options.direto'),
        value: 'direto',
    },
    {
        label: t('script.edit.form.media.options.facebook'),
        value: 'facebook',
    },
    {
        label: t('script.edit.form.media.options.gclid'),
        value: 'gclid',
    },
    {
        label: t('script.edit.form.media.options.organico'),
        value: 'organico',
    },
] as MultiSelectOption[];

const defaultMediaOption = mediaOptions.findIndex((option) => option.value === 'gclid');
const formState = reactive<ScriptFormState>({
    clientUrl: '',
    isActive: true,
    includeJquery: false,
    numbers: [
        {
            media: mediaOptions[defaultMediaOption],
            fromNumber: '',
            toNumber: '',
        },
    ],
});

const rules = computed<ValidationArgs<ScriptFormState>>(() => ({
    clientUrl: { required, maxLength: maxLength(255) },
    isActive: { required },
    includeJquery: { required },
    numbers: { required, minLength: minLength(1) },
}));

const v$: Ref<Validation<ValidationArgs<ScriptFormState>>> = useVuelidate(rules, formState);

const numberRules: ValidationArgs<ScriptNumberFormState> = {
    media: { required, maxLength: maxLength(250) },
    fromNumber: { required, maxLength: maxLength(20) },
    toNumber: { required, maxLength: maxLength(20) },
};

let vv$ = formState.numbers.map((number) => useVuelidate(numberRules, number));

watch(
    () => formState.numbers,
    () => {
        vv$ = formState.numbers.map((number) => useVuelidate(numberRules, number));
    },
    { deep: true },
);

const setLoading = (loading: boolean) => uiStore.setIsLoading(loading);

const fetchScriptById = async (id: number) => {
    try {
        setLoading(true);
        const { data } = await ScriptService.get<ScriptFormState>(id);
        const formData = {
            ...data,
            numbers: data.numbers.map((number) => ({
                ...number,
                media: mediaOptions.find((option) => option.value === number.media),
            })),
        };
        Object.assign(formState, formData);
    } catch (error) {
        toastServiceError(error);
    } finally {
        setLoading(false);
    }
};

onMounted(async () => {
    if (props.action === FormAction.EDIT) {
        const scriptId = Number(route.params.id);
        if (!isNaN(scriptId)) fetchScriptById(scriptId);
    }
});

const areAllValidationsCorrect = async (
    validationsArray: Ref<Validation<ValidationArgs<ScriptNumberFormState>>>[],
): Promise<boolean> => {
    const validationPromises = validationsArray.map((validation) => validation.value.$validate());
    await Promise.all(validationPromises);
    return validationsArray.every((validation) => validation.value.$invalid === false);
};

const removeNumber = (index: number) => {
    formState.numbers.splice(index, 1);
};

const addNumber = () => {
    formState.numbers.push({
        media: mediaOptions[0],
        fromNumber: '',
        toNumber: '',
    });
};

const onSubmit = async () => {
    const isFormCorrect = await v$.value.$validate();
    const isNumberFormCorrect = await areAllValidationsCorrect(vv$);

    if (!isFormCorrect || !isNumberFormCorrect) return;

    try {
        await setLoading(true);
        let response: AxiosResponse;

        const payload = {
            ...formState,
            numbers: formState.numbers.map((number) => ({
                ...number,
                media: number.media.value,
            })),
        };

        const handleServiceSuccess = (message: string, id: number | undefined) => {
            toast.success(message);
            v$.value.$reset();
            router.push({
                name: 'ViewScriptById',
                params: { id },
            });
        };

        if (props.action === FormAction.EDIT) {
            const id = Number(route.params.id);
            response = await ScriptService.update(id, payload);
            handleServiceSuccess(response.data.message, id);
        } else {
            response = await ScriptService.create(payload);
            handleServiceSuccess(response.data.message, response.data.result.id);
        }
    } catch (error) {
        toastServiceError(error);
    } finally {
        setLoading(false);
    }
};
</script>

<template>
    <form class="space-y-4 p-4 sm:p-6 lg:p-8" autocomplete="off" @submit.prevent="onSubmit">
        <UiPageHeader>
            <template #info>
                <h1 class="text-xl font-semibold text-gray-800">
                    {{ $t('script.' + props.action + '.title') }}
                </h1>
            </template>
            <template #actions>
                <div class="flex items-center space-x-4">
                    <UiBackButton :fallbackTo="{ name: 'ListScript' }" />
                    <UiButton
                        v-if="
                            (props.action == FormAction.CREATE && $can(Permission.CREATE_SCRIPT)) ||
                            (props.action == FormAction.EDIT && $can(Permission.EDIT_SCRIPT))
                        "
                        type="submit"
                        variant="primary"
                        text-variant="white"
                    >
                        {{ $t('core.actions.Save') }}
                    </UiButton>
                </div>
            </template>
        </UiPageHeader>
        <UiPanel class="space-y-4">
            <div class="gap-4 md:grid md:grid-cols-8">
                <UiTextInput
                    v-model="v$.clientUrl.$model"
                    name="clientUrl"
                    type="text"
                    :label="$t('script.labels.clientUrl')"
                    :errors="v$.clientUrl.$errors"
                    :required="v$.clientUrl.required !== undefined"
                    :disabled="props.action == FormAction.EDIT"
                    class="md:col-span-6 lg:col-span-4"
                    @blur="v$.clientUrl.$touch"
                />
                <UiCheckboxInput
                    v-model="formState.includeJquery"
                    name="includeJquery"
                    :label="$t('script.labels.includeJquery')"
                    class="col-span-2"
                />
                <UiCheckboxInput
                    v-model="formState.isActive"
                    name="isActive"
                    :label="$t('script.labels.isActive')"
                    class="col-span-2"
                />
            </div>
        </UiPanel>
        <div v-for="(number, index) in formState.numbers" :key="index">
            <UiPanel>
                <div v-if="vv$[index]" class="flex flex-col gap-6 md:flex-row">
                    <div class="flex-grow">
                        <label
                            for="media"
                            class="mb-2 block text-sm font-medium leading-7 text-gray-900"
                        >
                            {{ t('script.labels.media') }}
                        </label>
                        <UiMultiselect
                            v-model="number.media"
                            name="media"
                            label="label"
                            value-prop="value"
                            :options="mediaOptions"
                            :errors="vv$[index].value.media.$errors"
                            @blur="vv$[index].value.media.$touch"
                        />
                    </div>
                    <div class="flex-grow">
                        <UiTextInput
                            v-model="number.fromNumber"
                            name="fromNumber"
                            type="text"
                            :label="t('script.labels.fromNumber')"
                            :errors="vv$[index].value.fromNumber.$errors"
                            @blur="vv$[index].value.fromNumber.$touch"
                        />
                    </div>
                    <div class="flex-grow">
                        <UiTextInput
                            v-model="number.toNumber"
                            name="toNumber"
                            type="text"
                            :label="t('script.labels.toNumber')"
                            :errors="vv$[index].value.toNumber.$errors"
                            @blur="vv$[index].value.toNumber.$touch"
                        />
                    </div>
                    <div v-if="formState.numbers.length > 1" class="flex items-end">
                        <UiButton
                            v-tippy="t('core.actions.Remove')"
                            size=""
                            shape="rounded-full"
                            variant="inherit"
                            text-variant="danger"
                            shadow="none"
                            themed
                            class="focus:ring-danger ml-1 border-transparent text-sm shadow-none"
                            @click="removeNumber(index)"
                        >
                            <span class="sr-only"> {{ t('core.actions.Remove') }} </span>
                            <TrashIcon class="h-6 w-6" aria-hidden="true" />
                        </UiButton>
                    </div>
                </div>
            </UiPanel>
        </div>
        <div class="flex justify-end px-4 sm:px-0">
            <UiButton variant="white" text-variant="gray-500" class="ml-4" @click="addNumber">
                {{ $t('script.actions.addNumber') }}
            </UiButton>
        </div>
    </form>
</template>
